import { camelCase, snakeCase } from '@thecompaniesapi/string-helpers';
import { get, set } from 'lodash-es';
import sortKeys from 'sort-keys';

function camelCaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map((value) => camelCaseKeys(value));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelCaseKey = camelCase(key);
      result[camelCaseKey] = camelCaseKeys(obj[key]);
      return result;
    }, {});
  }
  return obj;
}
function convertNullableToEmptySpace(object) {
  for (const key of Object.keys(object)) {
    if (object[key] === null || object[key] === void 0) {
      object[key] = "";
    } else if (typeof object[key] === "object") {
      convertNullableToEmptySpace(object[key]);
    }
  }
  return object;
}
function deepPick(object, fieldsToKeep) {
  const formatted = {};
  for (const fieldToKeep of fieldsToKeep) {
    formatted[fieldToKeep] = get(object, fieldToKeep);
  }
  return formatted;
}
function getFrom(source) {
  return {
    get(key) {
      const value = get(source, key);
      if (value === null) {
        return;
      }
      return value;
    }
  };
}
function isEmpty(value) {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).length === 0;
  }
  return false;
}
function isObject(variable) {
  return typeof variable === "object" && variable !== null && !Array.isArray(variable);
}
function mergeAverageObjects(obj1, obj2) {
  Object.keys(obj2).forEach((key) => {
    const keyInt = Number.parseInt(key);
    if (!Number.isNaN(keyInt) || Array.isArray(obj1) && Array.isArray(obj2)) {
      if (Object.hasOwn(obj1, key)) {
        if (typeof obj1[key] === "object" && typeof obj2[key] === "object") {
          mergeAverageObjects(obj1[key], obj2[key]);
        } else if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
          const maxLength = Math.max(obj1[key].length, obj2[key].length);
          for (let index = 0; index < maxLength; index++) {
            const count = obj1[key][index] !== void 0 && obj2[key][index] !== void 0 ? 2 : 1;
            obj1[key][index] = ((obj1[key][index] || 0) + (obj2[key][index] || 0)) / count;
          }
        } else {
          obj1[key] = ((obj1[key] || 0) + obj2[key]) / 2;
        }
      } else {
        obj1[key] = obj2[key];
      }
    } else {
      obj1[key] = obj2[key];
    }
  });
  return obj1;
}
function mergeSumObjects(obj1, obj2) {
  const result = { ...obj1 };
  Object.keys(obj2).forEach((key) => {
    if (Object.hasOwn(result, key)) {
      if (typeof result[key] === "object" && typeof obj2[key] === "object") {
        result[key] = mergeSumObjects(result[key], obj2[key]);
      } else if (typeof result[key] === "string" || typeof obj2[key] === "string") {
        result[key] = 0;
      } else {
        result[key] = (result[key] || 0) + (obj2[key] || 0);
      }
    } else {
      result[key] = obj2[key];
    }
  });
  return result;
}
function removeEmptyKeys(object) {
  const result = {};
  for (const key in object) {
    const value = object[key];
    if (typeof value === "object" && value !== null && !Array.isArray(value) && Object.keys(value).length > 0 || Array.isArray(value) && value.some((item) => item !== null && item !== void 0 && item !== "") || value !== "" && value !== null && value !== void 0) {
      if (typeof value === "object" && !Array.isArray(value)) {
        const cleanedValue = removeEmptyKeys(value);
        if (Object.keys(cleanedValue).length > 0) {
          result[key] = cleanedValue;
        }
      } else if (Array.isArray(value)) {
        const cleanedArray = value.map((item) => typeof item === "object" && item !== null && !Array.isArray(item) ? removeEmptyKeys(item) : item).filter((item) => item !== null && item !== void 0 && item !== "");
        if (cleanedArray.length > 0) {
          result[key] = cleanedArray;
        }
      } else {
        result[key] = value;
      }
    }
  }
  return result;
}
function removeNullUndefinedEmpty(obj, keys = []) {
  if (typeof obj !== "object" && Array.isArray(obj)) {
    return obj;
  }
  for (const prop in obj) {
    if (obj[prop] === null || obj[prop] === void 0 || obj[prop] === "" || isEmpty(obj[prop]) || keys.includes(prop)) {
      delete obj[prop];
      continue;
    }
    if (typeof obj[prop] === "object" && !Array.isArray(obj[prop])) {
      obj[prop] = removeNullUndefinedEmpty(obj[prop], keys);
      if (isEmpty(obj[prop])) {
        delete obj[prop];
        continue;
      }
    }
    if (Array.isArray(obj[prop])) {
      obj[prop] = obj[prop].map((item) => removeNullUndefinedEmpty(item)).filter((item) => !isEmpty(item));
      if (isEmpty(obj[prop])) {
        delete obj[prop];
        continue;
      }
    }
  }
  return obj;
}
function setFrom(source, target) {
  return {
    set(keyFrom, keyTo, callback) {
      let value = get(source, keyFrom);
      if (callback) {
        value = callback(value);
      }
      if (value === null) {
        value = void 0;
      }
      if (value) {
        set(
          target,
          keyTo,
          value
        );
      }
    }
  };
}
function setIn(source) {
  return {
    set(key, value) {
      set(source, key, value);
      if (value === null) {
        return;
      }
      return source;
    }
  };
}
function snakeCaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map((value) => snakeCaseKeys(value));
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const snakeCaseKey = snakeCase(key);
      result[snakeCaseKey] = snakeCaseKeys(obj[key]);
      return result;
    }, {});
  }
  return obj;
}
function countKeysInObject(object) {
  let count = 0;
  function recursiveCount(obj) {
    if (Array.isArray(obj)) {
      for (const item of obj) {
        if (typeof item === "object" && item !== null) {
          recursiveCount(item);
        }
      }
    } else if (typeof obj === "object" && obj !== null) {
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (typeof value === "string" || typeof value === "number") {
          count++;
        } else if (typeof value === "object" && value !== null) {
          recursiveCount(value);
        }
      }
    }
  }
  recursiveCount(object);
  return count;
}
function sortKeysAlphabetically(object, deep = true) {
  return sortKeys(object, { deep });
}
function walkObject(schema, callback, previousKeys = [], jsonSchema = false) {
  if (Array.isArray(schema)) {
    return schema.map((item, index) => walkObject(item, callback, [...previousKeys, index.toString()], jsonSchema));
  }
  if (typeof schema === "object" && schema !== null) {
    for (const [key, value] of Object.entries(schema)) {
      const result = callback(
        key,
        value,
        jsonSchema ? [...previousKeys, key].filter((otherKey) => otherKey !== "properties").join(".") : [...previousKeys, key].join(".")
      );
      if (!result) {
        schema[key] = walkObject(value, callback, [...previousKeys, key], jsonSchema);
        continue;
      }
      schema[result.key] = result.value;
    }
  }
  return schema;
}

export { camelCaseKeys, convertNullableToEmptySpace, countKeysInObject, deepPick, getFrom, isEmpty, isObject, mergeAverageObjects, mergeSumObjects, removeEmptyKeys, removeNullUndefinedEmpty, setFrom, setIn, snakeCaseKeys, sortKeysAlphabetically, walkObject };
